/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AiToolProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Wrench, Plus, X, Edit2, Trash2, GripVertical,
    Code, Layers, Play, CheckCircle, AlertCircle, Loader2, Lock, PowerOff, Power,
} from 'lucide-react';
import {
    DndContext, closestCenter, DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Toggle } from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

import {
    AiToolProfile,
    AiToolProfileInput,
    getToolProfileById,
    createToolProfile,
    updateToolProfile,
    deleteToolProfile,
    getProfileToolItems,
    setProfileToolItems,
} from './api/aiToolProfilesApi';
import {
    AiAgentTool,
    AiAgentToolInput,
    createTool,
    updateTool,
    testTool,
    ToolTestResult,
} from '../ai-agents/api/aiAgentToolsApi';

import type { ToolProfilePreset } from './AiToolProfilesPage';

// -- Constants -----------------------------------------------------------------



const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const DEFAULT_TOOL: AiAgentToolInput = {
    name: '', description: '',
    parameters: { type: 'object', properties: {}, required: [] },
    endpointUrl: '', httpMethod: 'POST',
    authType: 'none', authHeader: 'Authorization', authValue: '',
    timeoutMs: 5000, enabled: true,
};

const DEFAULT_FORM: AiToolProfileInput = {
    name: '', description: '', enabled: true,
    authType: 'none', authHeader: 'Authorization', authValue: '', baseUrl: '',
};

// -- Parameter builder helpers -------------------------------------------------

interface JsonParam { name: string; type: string; description: string; required: boolean; }

function parseParamsToRows(params: Record<string, unknown>): JsonParam[] {
    const props = (params?.properties as Record<string, { type?: string; description?: string }>) || {};
    const req: string[] = (params?.required as string[]) || [];
    return Object.entries(props).map(([name, def]) => ({
        name, type: def?.type ?? 'string', description: def?.description ?? '', required: req.includes(name),
    }));
}

function rowsToParams(rows: JsonParam[]): Record<string, unknown> {
    const properties: Record<string, unknown> = {};
    const required: string[] = [];
    for (const r of rows) {
        properties[r.name] = { type: r.type, description: r.description };
        if (r.required) required.push(r.name);
    }
    return { type: 'object', properties, required };
}

function presetToForm(preset: ToolProfilePreset | null | undefined): AiToolProfileInput {
    if (!preset) return DEFAULT_FORM;
    const authType = preset.tools && preset.tools.length > 0 && preset.tools[0].authType ? preset.tools[0].authType : 'bearer';
    const authHeader = preset.tools && preset.tools.length > 0 && preset.tools[0].authHeader ? preset.tools[0].authHeader : 'Authorization';
    const authValue = preset.tools && preset.tools.length > 0 && preset.tools[0].authValue ? preset.tools[0].authValue : '';
    
    return {
        name: preset.profileName,
        description: preset.profileDescription,
        enabled: true,
        authType: authType as any,
        authHeader: authHeader,
        authValue: authValue,
        baseUrl: '',
    };
}

// -- SortableParamRow ---------------------------------------------------------

interface SortableParamRowProps {
    index: number;
    row: JsonParam;
    onChange: (i: number, field: keyof JsonParam, value: string | boolean) => void;
    onRemove: (i: number) => void;
}

function SortableParamRow({ index, row, onChange, onRemove }: SortableParamRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <div ref={setNodeRef} style={style} className="grid grid-cols-12 gap-2 items-center">
            {/* Drag handle */}
            <button type="button" className="col-span-1 flex justify-center cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded text-muted-foreground"
                {...attributes} {...listeners}>
                <GripVertical className="h-3.5 w-3.5" />
            </button>
            <input value={row.name} placeholder="param_name" onChange={e => onChange(index, 'name', e.target.value)}
                className="col-span-3 px-2 py-1.5 bg-background border border-input rounded text-xs font-mono focus:ring-1 focus:ring-primary/20" />
            <select value={row.type} onChange={e => onChange(index, 'type', e.target.value)}
                className="col-span-2 px-2 py-1.5 bg-background border border-input rounded text-xs focus:ring-1 focus:ring-primary/20">
                {['string', 'integer', 'number', 'boolean', 'object', 'array'].map(t => <option key={t}>{t}</option>)}
            </select>
            <input value={row.description} placeholder="Description" onChange={e => onChange(index, 'description', e.target.value)}
                className="col-span-4 px-2 py-1.5 bg-background border border-input rounded text-xs focus:ring-1 focus:ring-primary/20" />
            <div className="col-span-1 flex justify-center items-center">
                <input type="checkbox" checked={row.required}
                    onChange={e => onChange(index, 'required', e.target.checked)}
                    className="h-4 w-4 rounded accent-primary cursor-pointer" />
            </div>
            <button type="button" onClick={() => onRemove(index)}
                className="col-span-1 flex justify-center p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors">
                <Trash2 className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

// -- SortableToolRow ----------------------------------------------------------

interface SortableToolRowProps {
    index: number;
    tool: AiAgentTool | PendingTool;
    onEdit: () => void;
    onToggle: () => void;
    onRemove: () => void;
}

function SortableToolRow({ index, tool, onEdit, onToggle, onRemove }: SortableToolRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const saved = isSaved(tool);

    return (
        <div ref={setNodeRef} style={style}
            className={`grid grid-cols-12 gap-2 items-center px-2 py-2 rounded-lg border transition-colors
                ${!saved ? 'border-dashed border-primary/40 bg-primary/5' : 'border-border bg-background hover:bg-accent/20'}`}>

            {/* Drag handle */}
            <button type="button" className="col-span-1 flex justify-center cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded text-muted-foreground"
                {...attributes} {...listeners}>
                <GripVertical className="h-4 w-4" />
            </button>

            {/* Name + badges */}
            <div className="col-span-4 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                    <p className="text-sm font-mono font-medium truncate">{toolName(tool)}</p>
                    {!saved && <span className="text-[10px] px-1 py-0.5 rounded bg-primary/10 text-primary shrink-0">new</span>}
                    {toolHttpMethod(tool) && <span className="text-[10px] px-1 py-0.5 rounded bg-muted/70 text-muted-foreground font-mono shrink-0">{toolHttpMethod(tool)}</span>}
                </div>
            </div>

            {/* Description */}
            <p className="col-span-4 text-xs text-muted-foreground truncate">{toolDesc(tool)}</p>

            {/* Enabled toggle */}
            <div className="col-span-1 flex justify-center">
                <button type="button" onClick={onToggle}
                    title={toolEnabled(tool) ? 'Click to disable' : 'Click to enable'}
                    className={`p-1 rounded transition-colors ${
                        toolEnabled(tool)
                            ? 'text-green-500 hover:text-green-600 hover:bg-green-500/10'
                            : 'text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted'
                    }`}>
                    {toolEnabled(tool) ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                </button>
            </div>

            {/* Actions: Edit + Remove */}
            <div className="col-span-2 flex items-center justify-end gap-0.5">
                <button type="button" onClick={onEdit} title="Edit tool"
                    className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                    <Edit2 className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={onRemove} title="Remove from profile"
                    className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

// -- Tool Editor Modal (rendered via Portal to avoid clipping) ------------------

interface ToolEditorModalProps {
    tool: AiAgentToolInput;
    isNew: boolean;
    profileAuthType?: string | null;
    onSave: (data: AiAgentToolInput) => void;
    onClose: () => void;
    onTest?: (args: Record<string, unknown>) => Promise<ToolTestResult>;
    isSaving?: boolean;
}

function ToolEditorModal({ tool, isNew, profileAuthType, onSave, onClose, onTest, isSaving }: ToolEditorModalProps) {
    const [form, setForm] = useState<AiAgentToolInput>(tool);
    const [paramMode, setParamMode] = useState<'visual' | 'json'>('visual');
    const [paramRows, setParamRows] = useState<JsonParam[]>(parseParamsToRows(tool.parameters ?? {}));
    const [rawJson, setRawJson] = useState<string>(JSON.stringify(tool.parameters ?? {}, null, 2));
    const [jsonError, setJsonError] = useState<string>('');
    const [testArgs, setTestArgs] = useState<string>('{}');
    const [testResult, setTestResult] = useState<ToolTestResult | null>(null);
    const [testing, setTesting] = useState(false);
    const [showTest, setShowTest] = useState(false);

    const set = (k: keyof AiAgentToolInput, v: unknown) => setForm(p => ({ ...p, [k]: v }));

    const handleParamRowChange = (i: number, field: keyof JsonParam, value: string | boolean) => {
        const next = paramRows.map((r, idx) => idx === i ? { ...r, [field]: value } : r);
        setParamRows(next);
        setForm(p => ({ ...p, parameters: rowsToParams(next) }));
    };

    const addParamRow = () => {
        const next = [...paramRows, { name: '', type: 'string', description: '', required: false }];
        setParamRows(next);
        setForm(p => ({ ...p, parameters: rowsToParams(next) }));
    };

    const removeParamRow = (i: number) => {
        const next = paramRows.filter((_, idx) => idx !== i);
        setParamRows(next);
        setForm(p => ({ ...p, parameters: rowsToParams(next) }));
    };

    const handleParamDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const from = Number(active.id);
        const to = Number(over.id);
        const next = [...paramRows];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setParamRows(next);
        setForm(p => ({ ...p, parameters: rowsToParams(next) }));
    };

    const applyRawJson = () => {
        try {
            const parsed = JSON.parse(rawJson);
            setForm(p => ({ ...p, parameters: parsed }));
            setParamRows(parseParamsToRows(parsed));
            setJsonError('');
        } catch { setJsonError('Invalid JSON'); }
    };

    const handleTest = async () => {
        if (!onTest) return;
        setTesting(true);
        setTestResult(null);
        try {
            let args: Record<string, unknown> = {};
            try { args = JSON.parse(testArgs); } catch { args = {}; }
            setTestResult(await onTest(args));
        } finally { setTesting(false); }
    };

    const handleSave = () => {
        const data = paramMode === 'json'
            ? (() => { try { return { ...form, parameters: JSON.parse(rawJson) }; } catch { return form; } })()
            : form;
        onSave(data);
    };

    const modal = (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8">
            <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col">
                {/* -- Header -- */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card rounded-t-xl z-10">
                    <div className="flex items-center gap-2">
                        <Wrench className="h-5 w-5 text-primary" />
                        <h2 className="font-semibold">{isNew ? 'Add Tool' : 'Edit Tool'}</h2>
                    </div>
                    {profileAuthType && profileAuthType !== 'none' && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                            <Lock className="h-3 w-3 text-primary" />
                            Auth inherited from profile
                        </div>
                    )}
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-accent transition-colors ml-2">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-6 space-y-5">

                    {/* Name + Enabled */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label required className="mb-1.5 block text-sm font-medium">Tool Name</Label>
                            <input value={form.name} onChange={e => set('name', e.target.value.replace(/\s/g, '_').toLowerCase())}
                                placeholder="e.g. schedule_appointment"
                                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                            <p className="text-xs text-muted-foreground mt-1">snake_case, no spaces</p>
                        </div>
                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">Enabled</Label>
                            <Toggle value={form.enabled ?? true} onChange={v => set('enabled', v)} />
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <Label required className="mb-1.5 block text-sm font-medium">Description</Label>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={1}
                            placeholder="Describe what this tool does and when the AI should call it."
                            className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                    </div>

                    {/* Parameters */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium">Parameters (JSON Schema)</Label>
                            <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                                <button onClick={() => setParamMode('visual')} className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${paramMode === 'visual' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                    <Layers className="h-3 w-3" /> Visual
                                </button>
                                <button onClick={() => { setRawJson(JSON.stringify(form.parameters ?? {}, null, 2)); setParamMode('json'); }}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${paramMode === 'json' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                                    <Code className="h-3 w-3" /> JSON
                                </button>
                            </div>
                        </div>
                        {paramMode === 'visual' ? (
                            <div className="space-y-2">
                                {paramRows.length > 0 && (
                                    <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground px-1">
                                        <span className="col-span-1"></span>
                                        <span className="col-span-3">Name</span>
                                        <span className="col-span-2">Type</span>
                                        <span className="col-span-4">Description</span>
                                        <span className="col-span-1 text-center">Req</span>
                                    </div>
                                )}
                                <DndContext collisionDetection={closestCenter} onDragEnd={handleParamDragEnd}>
                                    <SortableContext items={paramRows.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                                        {paramRows.map((row, i) => (
                                            <SortableParamRow key={i} index={i} row={row} onChange={handleParamRowChange} onRemove={removeParamRow} />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                                <button onClick={addParamRow} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 mt-1">
                                    <Plus className="h-3.5 w-3.5" /> Add Parameter
                                </button>
                            </div>
                        ) : (
                            <div>
                                <textarea value={rawJson} onChange={e => setRawJson(e.target.value)} onBlur={applyRawJson} rows={8}
                                    className="w-full px-3 py-2 bg-muted border border-input rounded-md text-xs font-mono resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                                {jsonError && <p className="text-xs text-destructive mt-1">{jsonError}</p>}
                            </div>
                        )}
                    </div>

                    {/* Endpoint (auth is inherited from profile - no auth fields here) */}
                    <div className="border-t border-border pt-5">
                        <h3 className="text-sm font-semibold mb-3">Endpoint</h3>
                        <div className="grid grid-cols-4 gap-3">
                            <div className="col-span-3">
                                <Label className="mb-1.5 block text-xs text-muted-foreground">URL</Label>
                                <input value={form.endpointUrl ?? ''} onChange={e => set('endpointUrl', e.target.value)}
                                    placeholder="https://api.example.com/v1/action"
                                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                                <p className="text-xs text-muted-foreground mt-1">Auth and headers are set at the Profile level above</p>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-xs text-muted-foreground">HTTP Method</Label>
                                <select value={form.httpMethod ?? 'POST'} onChange={e => set('httpMethod', e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20">
                                    {HTTP_METHODS.map(m => <option key={m}>{m}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="mt-3 w-36">
                            <Label className="mb-1.5 block text-xs text-muted-foreground">Timeout (ms)</Label>
                            <input type="number" value={form.timeoutMs ?? 5000} onChange={e => set('timeoutMs', parseInt(e.target.value) || 5000)}
                                min={500} max={30000} step={500}
                                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20" />
                        </div>
                    </div>

                    {/* Test Panel (only for saved tools) */}
                    {!isNew && onTest && (
                        <div className="border-t border-border pt-4">
                            <button onClick={() => setShowTest(p => !p)}
                                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                                <Play className="h-4 w-4 text-primary" /> Test Execution {showTest ? '?' : '?'}
                            </button>
                            {showTest && (
                                <div className="mt-3 space-y-3">
                                    <div>
                                        <Label className="mb-1.5 block text-xs text-muted-foreground">Sample Arguments (JSON)</Label>
                                        <textarea value={testArgs} onChange={e => setTestArgs(e.target.value)} rows={4}
                                            className="w-full px-3 py-2 bg-muted border border-input rounded-md text-xs font-mono resize-none focus:ring-2 focus:ring-primary/20" />
                                    </div>
                                    <button onClick={handleTest} disabled={testing}
                                        className="flex items-center gap-2 px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-md transition-colors disabled:opacity-50">
                                        {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                        {testing ? 'Testing...' : 'Run Test'}
                                    </button>
                                    {testResult && (
                                        <div className="bg-muted rounded-lg p-3 space-y-2">
                                            <div className="flex items-center gap-2">
                                                {testResult.error || testResult.status === 0
                                                    ? <AlertCircle className="h-4 w-4 text-destructive" />
                                                    : testResult.status >= 200 && testResult.status < 300
                                                        ? <CheckCircle className="h-4 w-4 text-green-500" />
                                                        : <AlertCircle className="h-4 w-4 text-yellow-500" />}
                                                <span className="text-sm font-medium">{testResult.error ? 'Error' : `HTTP ${testResult.status}`}</span>
                                                <span className="text-xs text-muted-foreground ml-auto">{testResult.latencyMs}ms</span>
                                            </div>
                                            {testResult.error && <p className="text-xs text-destructive">{testResult.error}</p>}
                                            {testResult.body !== null && (
                                                <pre className="text-xs font-mono overflow-auto max-h-40 text-muted-foreground">
                                                    {typeof testResult.body === 'string' ? testResult.body : JSON.stringify(testResult.body, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 rounded-b-xl">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                    <button onClick={handleSave} disabled={!!isSaving || !form.name.trim() || !form.description.trim()}
                        className="flex items-center gap-2 px-5 py-2 text-sm bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors disabled:opacity-50">
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {isSaving ? 'Saving...' : 'Save Tool'}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

// -- Profile Tool type (saved or pending) -------------------------------------

interface PendingTool { _tempId: string; data: AiAgentToolInput; }
type ProfileTool = AiAgentTool | PendingTool;

function isSaved(t: ProfileTool): t is AiAgentTool { return 'id' in t; }
function toolName(t: ProfileTool) { return isSaved(t) ? t.name : t.data.name; }
function toolDesc(t: ProfileTool) { return isSaved(t) ? t.description : t.data.description; }
function toolEnabled(t: ProfileTool) { return isSaved(t) ? t.enabled : t.data.enabled; }
function toolKey(t: ProfileTool) { return isSaved(t) ? `s-${t.id}` : t._tempId; }
function toolHttpMethod(t: ProfileTool) { return isSaved(t) ? t.httpMethod : t.data.httpMethod; }

// -- Component ------------------------------------------------------------------

export default function AiToolProfileFormView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, refreshKey, i18n } = useI18n();
    const translateRef = useRef(t);
    useEffect(() => { translateRef.current = t; }, [t, refreshKey, i18n.language]);
    const { showSuccess, showError } = useNotifications();
    const showSuccessRef = useRef(showSuccess);
    const showErrorRef = useRef(showError);
    useEffect(() => {
        showSuccessRef.current = showSuccess;
        showErrorRef.current = showError;
    }, [showSuccess, showError]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const AUTH_TYPES = useMemo(() => [
        { value: 'none',    label: t('aiToolProfiles.authTypes.none') },
        { value: 'bearer',  label: t('aiToolProfiles.authTypes.bearer') },
        { value: 'api_key', label: t('aiToolProfiles.authTypes.api_key') },
        { value: 'basic',   label: t('aiToolProfiles.authTypes.basic') },
        { value: 'inherit_provider', label: 'Inherit from AI Provider' },
    ], [refreshKey, i18n.language]);

    const isNew = !id || id === 'new';
    const isEditMode = !isNew;
    const mode = isNew ? 'create' : 'edit';

    const incomingPreset = isNew ? (location.state as { preset?: ToolProfilePreset | null })?.preset : null;

    const [form, setForm] = useState<AiToolProfileInput>(presetToForm(incomingPreset));
    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState<number | null>(null);

    const [profileTools, setProfileTools] = useState<ProfileTool[]>(() => {
        if (incomingPreset?.tools?.length) {
            return incomingPreset.tools.map((data, i) => ({
                _tempId: `pending-${Date.now()}-${i}`,
                data,
            }));
        }
        return [];
    });



    const [editingTool, setEditingTool] = useState<ProfileTool | null>(null);
    const [showNewToolModal, setShowNewToolModal] = useState(false);
    const [toolSaving, setToolSaving] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const setF = (k: keyof AiToolProfileInput, v: unknown) =>
        setForm(prev => ({ ...prev, [k]: v }));

    // -- Load profile -----------------------------------------------------------

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        setSavedId(id as any);
        Promise.all([getToolProfileById(id as any), getProfileToolItems(id as any)])
            .then(([profile, tools]) => {
                // The Tool Profile table doesn't have auth DB columns; auth is strictly held in the Tool table.
                // We infer the global UI settings from the first tool in the profile.
                const firstTool = tools.length > 0 ? (tools[0] as AiAgentTool) : undefined;
                
                setForm({
                    name: profile.name,
                    description: profile.description ?? '',
                    enabled: profile.enabled,
                    authType: profile.authType ?? firstTool?.authType ?? 'none',
                    authHeader: profile.authHeader ?? firstTool?.authHeader ?? 'Authorization',
                    authValue: profile.authValue ?? firstTool?.authValue ?? '',
                    baseUrl: profile.baseUrl ?? '',
                });
                setProfileTools(tools);
            })
            .catch(() => showError('Failed to load tool profile'))
            .finally(() => setLoading(false));
    }, [id, isNew]);



    // -- Reorder / Remove -------------------------------------------------------

    const moveProfile = (i: number, dir: -1 | 1) => {
        const next = [...profileTools];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setProfileTools(next);
    };

    const removeProfile = (i: number) => setProfileTools(p => p.filter((_, idx) => idx !== i));

    // -- Add new tool (inline) -------------------------------------------------

    const handleNewTool = (data: AiAgentToolInput) => {
        const pending: PendingTool = { _tempId: `pending-${Date.now()}`, data };
        setProfileTools(p => [...p, pending]);
        setShowNewToolModal(false);
    };

    // -- Edit existing ---------------------------------------------------------

    const handleEditSave = async (data: AiAgentToolInput) => {
        if (!editingTool) return;
        // Merge profile-level auth into the tool before persisting
        const withAuth = mergeAuth(data);
        if (isSaved(editingTool)) {
            setToolSaving(true);
            try {
                const updated = await updateTool(editingTool.id, withAuth);
                setProfileTools(p => p.map(t => isSaved(t) && t.id === editingTool.id ? updated : t));
                showSuccess('Tool updated');
            } catch { showError('Failed to update tool'); }
            finally { setToolSaving(false); }
        } else {
            const tempId = editingTool._tempId;
            setProfileTools(p => p.map(t => !isSaved(t) && t._tempId === tempId ? { ...t, data } : t));
        }
        setEditingTool(null);
    };

    // -- Toggle tool enabled inline --------------------------------------------

    const toggleToolEnabled = (i: number) => {
        setProfileTools(prev => prev.map((t, idx) => {
            if (idx !== i) return t;
            if (isSaved(t)) return { ...t, enabled: !t.enabled };
            return { ...t, data: { ...t.data, enabled: !t.data.enabled } };
        }));
    };

    const handleToolsDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const from = Number(active.id);
        const to = Number(over.id);
        setProfileTools(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    // -- Auth merge helper -----------------------------------------------------

    const mergeAuth = (toolData: AiAgentToolInput): AiAgentToolInput => ({
        ...toolData,
        authType: form.authType ?? 'none',
        authHeader: form.authHeader ?? 'Authorization',
        authValue: form.authValue ?? '',
    });

    // -- Save -------------------------------------------------------------------

    const handleSaveInternal = useCallback(async (): Promise<AiToolProfile | undefined> => {
        if (!form.name.trim()) { showErrorRef.current?.(translateRef.current('aiToolProfiles.messages.nameRequired')); return undefined; }
        setSaving(true);
        try {
            const resolvedTools: AiAgentTool[] = [];
            for (const t of profileTools) {
                if (isSaved(t)) {
                    // Persist any inline changes (e.g. enabled toggle) and broadcast global auth to all tools
                    try {
                        const updated = await updateTool(t.id, {
                            name: t.name, description: t.description,
                            parameters: t.parameters, endpointUrl: t.endpointUrl,
                            httpMethod: t.httpMethod,
                            authType: form.authType ?? 'none',
                            authHeader: form.authHeader ?? 'Authorization',
                            authValue: form.authValue ?? '',
                            customHeaders: t.customHeaders,
                            timeoutMs: t.timeoutMs, enabled: t.enabled,
                        });
                        resolvedTools.push(updated);
                    } catch {
                        // If update fails, still include the tool as-is
                        resolvedTools.push(t);
                    }
                } else {
                    // Create new tool - if name conflicts (409) reuse existing
                    try {
                        const created = await createTool(mergeAuth(t.data));
                        resolvedTools.push(created);
                    } catch (e: any) {
                        const status = e?.response?.status ?? e?.status;
                        if (status === 409) {
                            // Tool with this name already exists - query and reuse it
                            const { listTools } = await import('../ai-agents/api/aiAgentToolsApi');
                            const res = await listTools({ limit: 200 });
                            const existing = res.data.find((x: AiAgentTool) => x.name === t.data.name);
                            if (existing) { resolvedTools.push(existing); continue; }
                        }
                        throw e;
                    }
                }
            }

            let profileId: number;
            let saved: AiToolProfile;
            if (isNew) {
                saved = await createToolProfile(form);
                profileId = saved.id;
                setSavedId(profileId);
            } else {
                saved = await updateToolProfile(id as any, form);
                profileId = id as any;
            }

            await setProfileToolItems(profileId, resolvedTools.map(t => t.id));
            setProfileTools(resolvedTools);
            showSuccessRef.current?.(isNew
                ? translateRef.current('aiToolProfiles.messages.createSuccess')
                : translateRef.current('aiToolProfiles.messages.saveSuccess'));
            return saved;
        } catch (e: any) {
            showErrorRef.current?.(e?.message ?? translateRef.current('aiToolProfiles.messages.saveError'));
            return undefined;
        } finally {
            setSaving(false);
        }
    }, [form, isNew, id, profileTools, showSuccess, showError]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/admin/ai-integration/tool-profiles',
        newPath: '/admin/ai-integration/tool-profiles/new',
        mode,
        getEditPath: (saved: AiToolProfile) =>
            saved?.id ? `/admin/ai-integration/tool-profiles/${saved.id}` : '/admin/ai-integration/tool-profiles',
    });

    // -- Delete -----------------------------------------------------------------

    const handleDelete = async () => {
        if (!savedId) return;
        setIsDeleting(true);
        try {
            await deleteToolProfile(savedId);
            showSuccessRef.current?.(translateRef.current('aiToolProfiles.messages.deleteSuccess'));
            navigate('/admin/ai-integration/tool-profiles');
        } catch { showErrorRef.current?.(translateRef.current('aiToolProfiles.messages.deleteError')); }
        finally { setIsDeleting(false); setShowDeleteModal(false); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // -- Render -----------------------------------------------------------------

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Wrench className="h-4 w-4 text-primary" />}
                title={isEditMode ? `${translateRef.current('aiToolProfiles.actions.edit')} - ${form.name || t('aiToolProfiles.deleteTitle')}` : t('aiToolProfiles.deleteTitle')}
                backButtonText={t('aiToolProfiles.actions.list')}
                onBack={() => navigate('/admin/ai-integration/tool-profiles')}
            />

            <div className="mt-8">
                <form onSubmit={e => { e.preventDefault(); handleSaveWithAction('save'); }} className="space-y-6">

                    {/* Preset banner */}
                    {isNew && incomingPreset && (
                        <div className="px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-2">
                            <span className="text-lg">{incomingPreset.emoji}</span>
                            <span className="text-sm text-muted-foreground">
                                Started from <strong className="text-foreground">{incomingPreset.name}</strong> template - review and customize before saving.
                            </span>
                        </div>
                    )}

                    {/* -- Profile Details -- */}
                    <FormBox title={t('aiToolProfiles.sections.profileDetails')} icon={<Wrench className="h-4 w-4" />}>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
                            {/* Row 1 */}
                            <SimpleTooltip content={t('aiToolProfiles.fields.name.label')} position="top">
                                <Label required className="text-left mt-2">{t('aiToolProfiles.fields.name.label')}</Label>
                            </SimpleTooltip>
                            <input value={form.name} onChange={e => setF('name', e.target.value)}
                                placeholder={t('aiToolProfiles.fields.name.placeholder')} required
                                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary" />

                            <SimpleTooltip content={t('aiToolProfiles.fields.description.label')} position="top">
                                <Label className="text-left mt-2">{t('aiToolProfiles.fields.description.label')}</Label>
                            </SimpleTooltip>
                            <input value={form.description ?? ''} onChange={e => setF('description', e.target.value)}
                                placeholder={t('aiToolProfiles.fields.description.placeholder')}
                                className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary" />

                            {/* Row 2 */}
                            <SimpleTooltip content={t('aiToolProfiles.fields.enabled.label')} position="top">
                                <Label className="text-left mt-1">{t('aiToolProfiles.fields.enabled.label')}</Label>
                            </SimpleTooltip>
                            <div className="mt-1"><Toggle value={form.enabled ?? true} onChange={v => setF('enabled', v)} /></div>
                        </div>
                    </FormBox>

                    {/* -- Global Authentication -- */}
                    <FormBox title={t('aiToolProfiles.sections.authentication')} icon={<Lock className="h-4 w-4" />}>
                        <p className="text-xs text-muted-foreground mb-4">
                            Configure authentication <strong>once</strong> - applied automatically to all tools in this profile.
                        </p>
                        <div className="flex flex-wrap gap-3 items-end">

                            {/* Auth Type */}
                            <div className="flex flex-col gap-1 min-w-[160px]">
                                <Label className="text-xs text-muted-foreground">{t('aiToolProfiles.fields.authType.label')}</Label>
                                <select value={form.authType ?? 'none'} onChange={e => setF('authType', e.target.value)}
                                    className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 h-9">
                                    {AUTH_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>

                            {/* Header Name (api_key only) */}
                            {form.authType === 'api_key' && (
                                <div className="flex flex-col gap-1 min-w-[140px]">
                                    <Label className="text-xs text-muted-foreground">{t('aiToolProfiles.fields.headerName.label')}</Label>
                                    <input value={form.authHeader ?? 'Authorization'} onChange={e => setF('authHeader', e.target.value)}
                                        placeholder="X-Api-Key"
                                        className="px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary h-9" />
                                </div>
                            )}

                            {/* Token / Secret */}
                            {form.authType !== 'none' && form.authType !== 'inherit_provider' && (
                                <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                                    <Label className="text-xs text-muted-foreground">
                                        {form.authType === 'bearer' ? 'Bearer Token' : form.authType === 'basic' ? 'user:password' : 'API Key Value'}
                                    </Label>
                                    <input type="password" value={form.authValue ?? ''} onChange={e => setF('authValue', e.target.value)}
                                        placeholder="Your secret token or key"
                                        className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary h-9" />
                                </div>
                            )}
                            
                            {form.authType === 'inherit_provider' && (
                                <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                                    <Label className="text-xs text-muted-foreground">API Key</Label>
                                    <input type="text" value="Inherited automatically from Agent's AI Provider" disabled
                                        className="w-full px-3 py-2 bg-muted border border-input rounded-md text-sm text-muted-foreground h-9 cursor-not-allowed" />
                                </div>
                            )}

                            {/* Base URL */}
                            <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
                                <Label className="text-xs text-muted-foreground">{t('aiToolProfiles.fields.baseUrl.label')} <span className="opacity-60">(optional)</span></Label>
                                <input value={form.baseUrl ?? ''} onChange={e => setF('baseUrl', e.target.value)}
                                    placeholder={t('aiToolProfiles.fields.baseUrl.placeholder')}
                                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary h-9" />
                            </div>

                        </div>
                    </FormBox>

                    {/* -- Tools in Profile -- */}
                    <FormBox
                        title={`${t('aiToolProfiles.sections.toolsInProfile')}${profileTools.length > 0 ? ` (${profileTools.length})` : ''}`}
                        icon={<Wrench className="h-4 w-4" />}
                        headerContent={
                            <button type="button" onClick={() => setShowNewToolModal(true)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors">
                                <Plus className="h-3.5 w-3.5" /> {t('aiToolProfiles.actions.addTool')}
                            </button>
                        }
                    >
                        {profileTools.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground text-sm">
                                <Wrench className="h-8 w-8 mx-auto mb-2 opacity-20" />
                                <p>No tools yet.</p>
                                <p className="text-xs mt-1">Click <strong>+ Add Tool</strong> to create the first tool for this profile.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {/* Header row */}
                                <div className="grid grid-cols-12 gap-2 text-xs text-muted-foreground px-2 pb-1 border-b border-border">
                                    <span className="col-span-1"></span>
                                    <span className="col-span-4">Name</span>
                                    <span className="col-span-4">Description</span>
                                    <span className="col-span-1 text-center">On</span>
                                    <span className="col-span-2"></span>
                                </div>
                                <DndContext collisionDetection={closestCenter} onDragEnd={handleToolsDragEnd}>
                                    <SortableContext items={profileTools.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                                        {profileTools.map((tool, i) => (
                                            <SortableToolRow
                                                key={toolKey(tool)}
                                                index={i}
                                                tool={tool}
                                                onEdit={() => setEditingTool(tool)}
                                                onToggle={() => toggleToolEnabled(i)}
                                                onRemove={() => {
                                                    setProfileTools(p => p.filter((_, idx) => idx !== i));
                                                }}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}
                    </FormBox>
                </form>
            </div>

            {/* Fixed Action Bar */}
            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={() => { if (isNew) { setForm({ name: '', description: '', enabled: true, authType: 'none', authHeader: 'Authorization', authValue: '', baseUrl: '' }); setProfileTools([]); } else { window.location.reload(); } }}
                onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
                isEditMode={isEditMode}
                isSaving={saving}
                useSaveDropdown={true}
                saveDropdownStorageKey="aiToolProfiles.saveAction"
                saveDisabled={saving || !form.name.trim()}
            />

            {/* New Tool Modal (Portal) */}
            {showNewToolModal && (
                <ToolEditorModal
                    tool={DEFAULT_TOOL}
                    isNew={true}
                    profileAuthType={form.authType}
                    onSave={handleNewTool}
                    onClose={() => setShowNewToolModal(false)}
                />
            )}

            {/* Edit Tool Modal (Portal) */}
            {editingTool && (
                <ToolEditorModal
                    tool={isSaved(editingTool) ? {
                        name: editingTool.name, description: editingTool.description,
                        parameters: editingTool.parameters, endpointUrl: editingTool.endpointUrl,
                        httpMethod: editingTool.httpMethod, authType: editingTool.authType,
                        authHeader: editingTool.authHeader, authValue: editingTool.authValue,
                        timeoutMs: editingTool.timeoutMs, enabled: editingTool.enabled,
                    } : editingTool.data}
                    isNew={false}
                    profileAuthType={form.authType}
                    onSave={handleEditSave}
                    onClose={() => setEditingTool(null)}
                    onTest={isSaved(editingTool) ? (args) => testTool(editingTool.id, args, {
                        authType: form.authType ?? 'none',
                        authHeader: form.authHeader ?? 'Authorization',
                        authValue: form.authValue ?? ''
                    }) : undefined}
                    isSaving={toolSaving}
                />
            )}

            {/* Delete Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title={t('aiToolProfiles.deleteTitle')}
                message={`${translateRef.current('aiToolProfiles.messages.deleteConfirm', { name: form.name })}`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                isLoading={isDeleting}
                type="danger"
            />
        </div>
    );
}
